import * as roleApi from '@/services/common/role';
import * as userApi from '@/services/common/user';
import { clearToken } from '@/utils/client';
import { LogoutOutlined, SwapOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings, MenuDataItem } from '@ant-design/pro-components';
import '@ant-design/v5-patch-for-react-19';
import type { RuntimeConfig } from '@umijs/max';
import { history } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Dropdown, Spin, Tag } from 'antd';
import { createStyles } from 'antd-style';
import defaultSettings from '../config/defaultSettings';
import routes from '../config/routes';
const ROUTES = {
  LOGIN: '/login',
  WELCOME: '/welcome',
  FORBIDDEN: '/403',
} as const;
interface AppRoute {
  path?: string;
  permissionPath?: string;
  routes?: AppRoute[];
}
interface InitialState {
  currentUser?: userApi.GetDetailRes;
  currentRoleId?: number;
  authorizedPaths?: string[];
  menuData?: MenuDataItem[];
  settings?: Partial<LayoutSettings>;
  switchRole?: (roleId: number) => void;
}
const defaultInitialState: InitialState = {
  menuData: [],
  authorizedPaths: [],
  settings: defaultSettings as Partial<LayoutSettings>,
};
const normalizePath = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);
const matchRoutePath = (routePath: string, pathname: string) => {
  const routeParts = normalizePath(routePath).split('/').filter(Boolean);
  const pathParts = normalizePath(pathname).split('/').filter(Boolean);
  return (
    routeParts.length === pathParts.length &&
    routeParts.every((part, index) => part.startsWith(':') || part === pathParts[index])
  );
};
const getRoutePermissionPath = (pathname: string, routeList: AppRoute[] = routes as AppRoute[]): string | undefined => {
  for (const route of routeList) {
    if (route.path && route.permissionPath && matchRoutePath(route.path, pathname)) {
      return route.permissionPath;
    }
    const childPermissionPath = route.routes ? getRoutePermissionPath(pathname, route.routes) : undefined;
    if (childPermissionPath) return childPermissionPath;
  }
  return undefined;
};
const useStyles = createStyles(({ token }) => ({
  action: {
    'display': 'flex',
    'height': '48px',
    'marginLeft': 'auto',
    'alignItems': 'center',
    'overflow': 'hidden',
    'padding': '0 8px',
    'cursor': 'pointer',
    'borderRadius': token.borderRadius,
    '&:hover': { backgroundColor: token.colorBgTextHover },
  },
}));
const AvatarDropdown = ({ children, initialState }: { children?: React.ReactNode; initialState?: InitialState }) => {
  const { styles } = useStyles();
  const currentUser = initialState?.currentUser;
  const currentRoleId = initialState?.currentRoleId;
  const roles = currentUser?.roles ?? [];
  const hasUser = currentUser?.nickname || currentUser?.username;
  if (!initialState || !hasUser) {
    return (
      <span className={styles.action}>
        <Spin size="small" style={{ margin: '0 8px' }} />
      </span>
    );
  }
  const menuItems: MenuProps['items'] = [];
  if (roles.length > 0 && initialState.switchRole) {
    const roleItems: MenuProps['items'] = roles.map((role: any) => ({
      key: `role-${role.id}`,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <span>{role.name ?? role.code ?? `角色${role.id}`}</span>
          {role.id === currentRoleId && (
            <Tag color="blue" style={{ margin: 0 }}>
              当前
            </Tag>
          )}
        </div>
      ),
      onClick: () => {
        if (role.id !== currentRoleId) {
          initialState.switchRole?.(role.id);
        }
      },
    }));
    menuItems.push({
      type: 'group',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SwapOutlined />
          切换角色
        </span>
      ),
      children: roleItems,
    });
    menuItems.push({ type: 'divider' });
  }
  menuItems.push({
    key: 'logout',
    icon: <LogoutOutlined />,
    label: '退出登录',
    onClick: () => {
      clearToken();
      localStorage.removeItem('currentRoleId');
      const { pathname, search } = window.location;
      if (pathname !== '/login' && !new URL(window.location.href).searchParams.get('redirect')) {
        history.replace({
          pathname: '/login',
          search: `?redirect=${pathname}${search}`,
        });
      }
      window.location.reload();
    },
  });
  return (
    <Dropdown
      menu={{
        items: menuItems,
        selectedKeys: currentRoleId ? [`role-${currentRoleId}`] : [],
      }}
    >
      {children}
    </Dropdown>
  );
};
export async function getInitialState(): Promise<InitialState> {
  const { location } = history;
  if (location.pathname === ROUTES.LOGIN) {
    return defaultInitialState;
  }
  const token = localStorage.getItem('token');
  if (!token) {
    history.push(ROUTES.LOGIN);
    return defaultInitialState;
  }
  let currentUser: userApi.GetDetailRes | undefined;
  try {
    currentUser = await userApi.getDetail();
  } catch {
    history.push(ROUTES.LOGIN);
  }
  if (!currentUser) {
    return defaultInitialState;
  }
  const savedRoleId = Number(localStorage.getItem('currentRoleId'));
  const firstRoleId = currentUser.roles?.[0]?.id;
  const currentRoleId =
    savedRoleId && currentUser.roles?.some((r: any) => r.id === savedRoleId) ? savedRoleId : (firstRoleId ?? undefined);
  const authorizedPaths = currentRoleId
    ? (await roleApi.getRoleMenus({ roleId: String(currentRoleId) }))
        .map((menu: { id: number; parentId: number | null; path: string | null }) => menu.path)
        .filter((path: string | null): path is string => typeof path === 'string')
    : [];
  const switchRole = (roleId: number) => {
    localStorage.setItem('currentRoleId', String(roleId));
    window.location.reload();
  };
  return {
    ...defaultInitialState,
    currentUser,
    currentRoleId,
    authorizedPaths,
    switchRole,
  };
}
export const layout: RuntimeConfig = ({
  initialState,
  setInitialState,
}: {
  initialState?: InitialState;
  setInitialState?: (state: InitialState | ((prevState: InitialState | undefined) => InitialState | undefined)) => void;
}) => {
  const displayName = initialState?.currentUser?.nickname || initialState?.currentUser?.username;
  const hasPathPermission = (path: string) => {
    if (!path || path === ROUTES.LOGIN) return true;
    if (!initialState?.currentUser) return false;
    if (initialState.currentUser.username === 'admin') {
      const excludedModules = process.env.UMI_APP_EXCLUDED_MODULES;
      return !excludedModules?.split(',').some((m) => path.startsWith(`/${m.trim()}`));
    }
    const permissionPath = getRoutePermissionPath(path);
    return (
      path === ROUTES.WELCOME ||
      initialState.authorizedPaths?.includes(path) ||
      (permissionPath ? initialState.authorizedPaths?.includes(permissionPath) : false)
    );
  };
  return {
    ...initialState?.settings,
    actionsRender: () => [],
    menuDataRender: (menuData: MenuDataItem[]) => {
      if (!initialState?.currentUser) return [];
      const filteredMenu = menuData.filter((menu: MenuDataItem) => {
        const hasPermission = hasPathPermission(menu.path || '');
        if (menu.children?.length) {
          menu.children = menu.children.filter((child: MenuDataItem) => hasPathPermission(child.path || ''));
          return menu.children.length > 0 || hasPermission;
        }
        return hasPermission;
      });
      if (initialState.currentUser.username === 'admin') {
        requestAnimationFrame(() => {
          setInitialState?.((s) => (s ? { ...s, menuData: filteredMenu } : s));
        });
      }
      return filteredMenu;
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <span className="anticon">{displayName}</span>,
      render: (_: unknown, avatarChildren: React.ReactNode) => (
        <AvatarDropdown initialState={initialState}>{avatarChildren}</AvatarDropdown>
      ),
    },
    waterMarkProps: {
      content: displayName,
    },
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== ROUTES.LOGIN) {
        history.push(ROUTES.LOGIN);
      }
      if (!hasPathPermission(location.pathname)) {
        history.push(ROUTES.FORBIDDEN);
      }
    },
  };
};
